import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MEDICAL_FAQ_CATEGORIES } from "@/content/medical-corpus/faq-defs";

export const Route = createFileRoute("/medical/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/medical-corpus");
    return { faqs: m.MEDICAL_FAQ_TH };
  },
  component: MedicalFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `เอกสารทางการแพทย์ ประกันสุขภาพ วีซ่ารักษาพยาบาล — FAQ ${faqs.length} ข้อ`,
        description:
          "คำถามที่พบบ่อยเรื่องใบรับรองแพทย์ตามแบบแพทยสภา เวชระเบียนและใบสรุปการรักษา สมุดวัคซีน ICVP การแปลและรับรองนิติกรณ์เอกสารแพทย์ วีซ่าเข้ามารักษาพยาบาลและผู้ติดตาม ประกันสุขภาพที่วีซ่า O-A/O-X/LTR กำหนด การเคลมประกันข้ามประเทศ สูติบัตร-มรณบัตร และหนังสือมอบอำนาจทางการแพทย์",
        path: "/medical/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/medical/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ เอกสารการแพทย์และประกันสุขภาพ", url: `${site}/medical/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/medical/faq")) },
      ],
    };
  },
});

function MedicalFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ เอกสารการแพทย์และประกันสุขภาพ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          เอกสารทางการแพทย์ ประกันสุขภาพ และวีซ่ารักษาพยาบาล — คำถามที่พบบ่อย {faqs.length} ข้อ
        </h1>
        <p className="mb-6 text-muted-foreground">
          คำตอบอ้างอิงแพทยสภา (tmc.or.th) กรมสนับสนุนบริการสุขภาพ (hss.moph.go.th) พระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล
          พ.ศ. 2562 พระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 พระราชบัญญัติสุขภาพแห่งชาติ พ.ศ. 2550 กรมการกงสุล
          (consular.mfa.go.th) สำนักงานตรวจคนเข้าเมือง (immigration.go.th) สำนักงาน คปภ. (oic.or.th) และกฎอนามัยระหว่างประเทศ
          ค.ศ. 2005 ขององค์การอนามัยโลก ข้อมูลนี้เป็นข้อมูลทั่วไป ระเบียบและเงื่อนไขอาจเปลี่ยนแปลงได้
          โปรดตรวจสอบกับหน่วยงานหรือสถานพยาบาลก่อนดำเนินการ
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/medical/faq" className="text-primary underline">Medical documents, insurance &amp; treatment visa FAQ →</a>
        </div>
        <section aria-label="หมวดคำถาม" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">เลือกอ่านตามหมวด</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {MEDICAL_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/medical/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleTh}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">ข้อ {i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามรายการเอกสาร การแปลและรับรองนิติกรณ์เอกสารแพทย์ และการเตรียมชุดเคลมประกันได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="medical" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
